import { supabase } from './supabaseClient';
import { ActiveItem, ShopItem, ShopItemEffect } from '../types';
import { getShopItemById } from './shop-catalog';
import { logWarn, logInfo } from './logger';

const STORAGE_KEY_INVENTORY = (userId: string) => `dopamind_inventory_${userId}`;
const STORAGE_KEY_ACTIVE_ITEMS = (userId: string) => `dopamind_active_items_${userId}`;

type InventoryRow = {
  user_id: string;
  item_id: string;
  item_name: string;
  item_type: ShopItem['type'];
  effect_type: ShopItemEffect['type'] | null;
  effect_duration_hours: number | null;
  effect_multiplier: number | null;
  is_active: boolean;
  activated_at: string | null;
  expires_at: string | null;
};

const saveInventoryCache = (userId: string, inventory: string[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_INVENTORY(userId), JSON.stringify(inventory));
};

const saveActiveItemsCache = (userId: string, activeItems: ActiveItem[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_ACTIVE_ITEMS(userId), JSON.stringify(activeItems));
};

const normalizeActiveItems = (items: ActiveItem[]) => {
  const now = new Date().toISOString();
  return items.filter((item) => item.expiresAt > now);
};

const mapRowToActiveItem = (row: InventoryRow): ActiveItem | null => {
  if (!row.is_active || !row.activated_at || !row.expires_at || !row.effect_type) {
    return null;
  }

  const catalogItem = getShopItemById(row.item_id);
  const baseEffect = catalogItem?.effect;

  if (!baseEffect) {
    return null;
  }

  return {
    itemId: row.item_id,
    itemName: row.item_name,
    effect: {
      ...baseEffect,
      type: row.effect_type,
      duration: row.effect_duration_hours ?? baseEffect.duration,
      multiplier: row.effect_multiplier ?? baseEffect.multiplier
    },
    activatedAt: row.activated_at,
    expiresAt: row.expires_at
  };
};

const hydrateLocalCacheFromRows = (userId: string, rows: InventoryRow[]) => {
  const ownedItems = rows.map((row) => row.item_id);
  const activeItems = normalizeActiveItems(
    rows
      .map((row) => mapRowToActiveItem(row))
      .filter(Boolean) as ActiveItem[]
  );

  saveInventoryCache(userId, ownedItems);
  saveActiveItemsCache(userId, activeItems);
};

const createFallbackActiveItem = (itemId: string, itemName: string, effect: ShopItemEffect): ActiveItem => {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + (effect.duration || 24) * 60 * 60 * 1000);

  return {
    itemId,
    itemName,
    effect,
    activatedAt: now.toISOString(),
    expiresAt: expiresAt.toISOString()
  };
};

// Inventory Management
export const getUserInventory = (userId: string): string[] => {
  if (typeof window === 'undefined') return [];
  const stored = window.localStorage.getItem(STORAGE_KEY_INVENTORY(userId));
  return stored ? JSON.parse(stored) : [];
};

export const syncInventoryCache = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('user_inventory')
    .select('user_id, item_id, item_name, item_type, effect_type, effect_duration_hours, effect_multiplier, is_active, activated_at, expires_at')
    .eq('user_id', userId);

  if (error) {
    logWarn('inventory', 'Falha ao sincronizar inventario no Supabase. Mantendo cache local.', error);
    return false;
  }

  hydrateLocalCacheFromRows(userId, (data as InventoryRow[]) || []);
  logInfo('inventory', 'Inventario sincronizado com sucesso.', { userId, items: (data as InventoryRow[])?.length || 0 });
  return true;
};

export const addToInventory = async (userId: string, item: ShopItem): Promise<boolean> => {
  const inventory = getUserInventory(userId);
  if (!inventory.includes(item.id)) {
    saveInventoryCache(userId, [...inventory, item.id]);
  }

  const { error } = await supabase
    .from('user_inventory')
    .upsert({
      user_id: userId,
      item_id: item.id,
      item_name: item.name,
      item_type: item.type,
      effect_type: item.effect?.type || null,
      effect_duration_hours: item.effect?.duration || null,
      effect_multiplier: item.effect?.multiplier || null
    }, { onConflict: 'user_id,item_id' });

  if (error) {
    logWarn('inventory', 'Item salvo apenas no cache local.', error);
    return false;
  }

  await syncInventoryCache(userId);
  return true;
};

export const removeFromInventory = async (userId: string, itemId: string): Promise<boolean> => {
  const inventory = getUserInventory(userId);
  saveInventoryCache(userId, inventory.filter((id) => id !== itemId));

  const activeItems = getActiveItems(userId).filter((item) => item.itemId !== itemId);
  saveActiveItemsCache(userId, activeItems);

  const { error } = await supabase
    .from('user_inventory')
    .delete()
    .eq('user_id', userId)
    .eq('item_id', itemId);

  if (error) {
    logWarn('inventory', 'Remocao feita so no cache local.', error);
    return false;
  }

  return true;
};

// Active Items Management
export const getActiveItems = (userId: string): ActiveItem[] => {
  if (typeof window === 'undefined') return [];
  const stored = window.localStorage.getItem(STORAGE_KEY_ACTIVE_ITEMS(userId));
  if (!stored) return [];
  
  const items: ActiveItem[] = JSON.parse(stored);
  const now = new Date().toISOString();
  
  // Filter out expired items
  const active = items.filter(item => item.expiresAt > now);
  
  // Update storage if any expired
  if (active.length !== items.length) {
    saveActiveItemsCache(userId, active);
  }
  
  return active;
};

export const activateItem = async (
  userId: string,
  itemId: string,
  itemName: string,
  effect: ShopItemEffect
): Promise<boolean> => {
  const activeItems = getActiveItems(userId);
  
  // Check if same effect type is already active
  if (effect.type !== 'cosmetic') {
    const existing = activeItems.find((item) => item.effect.type === effect.type);
    if (existing) {
      return false;
    }
  }

  const newActiveItem = createFallbackActiveItem(itemId, itemName, effect);
  saveActiveItemsCache(userId, [...activeItems, newActiveItem]);

  const { error } = await supabase
    .from('user_inventory')
    .update({
      is_active: true,
      activated_at: newActiveItem.activatedAt,
      expires_at: newActiveItem.expiresAt
    })
    .eq('user_id', userId)
    .eq('item_id', itemId);

  if (error) {
    logWarn('inventory', 'Ativacao feita so no cache local.', error);
    return true;
  }

  await syncInventoryCache(userId);
  return true;
};

export const hasActiveEffect = (userId: string, effectType: ShopItemEffect['type']): boolean => {
  const activeItems = getActiveItems(userId);
  return activeItems.some((item) => item.effect.type === effectType);
};

export const getActiveXpMultiplier = (userId: string): number => {
  const activeItems = getActiveItems(userId);
  const boostItem = activeItems.find((item) => item.effect.type === 'xp_boost');
  return boostItem?.effect.multiplier || 1;
};

export const consumeStreakFreeze = async (userId: string): Promise<boolean> => {
  const activeItems = getActiveItems(userId);
  const freezeIndex = activeItems.findIndex((item) => item.effect.type === 'streak_freeze');
  
  if (freezeIndex === -1) return false;
  
  // Aqui trocamos o consumo puramente local por cache + tentativa no banco.
  activeItems.splice(freezeIndex, 1);
  saveActiveItemsCache(userId, activeItems);

  const { error } = await supabase
    .from('user_inventory')
    .update({
      is_active: false,
      expires_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('effect_type', 'streak_freeze')
    .eq('is_active', true);

  if (error) {
    logWarn('inventory', 'Consumo do streak freeze ficou so no cache local.', error);
  }

  return true;
};
